import PropTypes from "prop-types";
import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from "react";
import { io } from "socket.io-client";
import { useAuth } from "./AuthContext";
import { useWsspAuth } from './WsspAuthContext';
import { useNotification } from './NotificationContext';

const WhatsAppSocketContext = createContext(null);

export const useWhatsAppSocket = () => {
  return useContext(WhatsAppSocketContext);
};

export const WhatsAppSocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const { user } = useAuth();
  const { isAuthenticated, wsspToken } = useWsspAuth();
  const { showNotification } = useNotification();
  
  const socketRef = useRef(null);
  const isConnectingRef = useRef(false);
  const userRef = useRef(user);
  const wsspTokenRef = useRef(wsspToken);
  const isAuthenticatedRef = useRef(isAuthenticated);
  const mountedRef = useRef(true);
  const reconnectAttemptsRef = useRef(0);
  
  userRef.current = user;
  wsspTokenRef.current = wsspToken;
  isAuthenticatedRef.current = isAuthenticated;

  useEffect(() => {
    const handleMainLogout = () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        setSocket(null);
      }
      isConnectingRef.current = false;
    };

    window.addEventListener('auth:logout', handleMainLogout);

    return () => {
      window.removeEventListener('auth:logout', handleMainLogout);
    };
  }, []);

  const createWhatsAppConnection = useCallback(() => {
    if (!isAuthenticatedRef.current || !wsspTokenRef.current) {
      return null;
    }

    if (isConnectingRef.current) {
      return socketRef.current;
    }

    if (socketRef.current?.connected) {
      return socketRef.current;
    }

    isConnectingRef.current = true;

    const socketUrl = process.env.REACT_APP_ENV === 'production'
      ? process.env.REACT_APP_WSSP_SOCKET_URL_PRODUCTION
      : process.env.REACT_APP_WSSP_SOCKET_URL;
    
    const newSocket = io(socketUrl, {
      transports: ["websocket", "polling"],
      reconnectionAttempts: 5,
      reconnectionDelay: 3000,
      reconnectionDelayMax: 10000,
      timeout: 20000,
      forceNew: true,
      autoConnect: false,
      auth: {
        token: wsspTokenRef.current,
      },
      query: {
        userId: userRef.current?.id || 'frontend-user',
        userEmail: userRef.current?.email || 'frontend@app.com',
        userName: userRef.current 
          ? `${userRef.current.first_name.trim()} ${userRef.current.first_last_name.trim()} ${
              userRef.current.second_last_name || ''
            }`.trim()
          : 'Usuario Frontend',
        userRole: userRef.current?.role_name || 'frontend-user',
        documentNumber: userRef.current?.document_number || 'N/A',
        clientVersion: '1.0.0',
        connectionTime: Date.now(),
        isWhatsAppClient: true,
        service: 'whatsapp'
      },
    });

    const setupWhatsAppSocketEvents = () => {
      newSocket.on('connect', () => {
        if (!mountedRef.current) return;
        
        isConnectingRef.current = false;
        reconnectAttemptsRef.current = 0;
        socketRef.current = newSocket;
        setSocket(newSocket);
        
        showNotification({
          message: 'Sistema de WhatsApp conectado',
          severity: 'success',
          position: { vertical: 'bottom', horizontal: 'left' },
          autoHideDuration: 3000,
        });
      });

      newSocket.on('disconnect', () => {
        if (!mountedRef.current) return;
      });

      newSocket.on('connect_error', (error) => {
        if (!mountedRef.current) return;
        
        isConnectingRef.current = false;
        reconnectAttemptsRef.current++;
        
        if (error.message.includes('token') || error.message.includes('authentication')) {
          window.dispatchEvent(new CustomEvent('wssp:auth-required'));
        }
        
        if (reconnectAttemptsRef.current >= 5) {
          showNotification({
            message: 'No se puede conectar al sistema de WhatsApp',
            severity: 'error',
            position: { vertical: 'bottom', horizontal: 'left' },
            autoHideDuration: 5000,
          });
        }
      });

      newSocket.on('authentication_error', (data) => {
        if (!mountedRef.current) return;
        
        window.dispatchEvent(new CustomEvent('wssp:auth-required', {
          detail: { message: data.message || 'Autenticacion requerida' }
        }));
      });

      newSocket.on('reconnect', (attemptNumber) => {
        if (!mountedRef.current) return;
        
        reconnectAttemptsRef.current = 0;
        
        showNotification({
          message: 'Reconectado al sistema de WhatsApp',
          severity: 'success',
          position: { vertical: 'bottom', horizontal: 'left' },
          autoHideDuration: 2000,
        });
      });

      newSocket.on('reconnect_failed', () => {
        if (!mountedRef.current) return;
        
        showNotification({
          message: 'No se pudo reconectar al sistema de WhatsApp',
          severity: 'error',
          position: { vertical: 'bottom', horizontal: 'left' },
          autoHideDuration: 5000,
        });
      });

      newSocket.on('token_refresh', (data) => {
        if (!mountedRef.current) return;
        
        if (data.token) {
          localStorage.setItem('wssp_token', data.token);
          wsspTokenRef.current = data.token;
          window.dispatchEvent(
            new CustomEvent('wssp:token-renewed', { detail: { token: data.token } })
          );
        }
      });

      newSocket.on('whatsapp:authenticated', () => {
        if (!mountedRef.current) return;
        // WhatsApp autenticado exitosamente
      });

      newSocket.on('whatsapp:needsAuthentication', () => {
        if (!mountedRef.current) return;
        // WhatsApp requiere autenticacion
      });

      newSocket.on('whatsapp:ready', () => {
        if (!mountedRef.current) return;
        // WhatsApp listo para usar
      });

      newSocket.on('whatsapp-qr', () => {
        if (!mountedRef.current) return;
        // Codigo QR recibido
      });

      newSocket.on('whatsapp-status', () => {
        if (!mountedRef.current) return;
        // Estado de WhatsApp actualizado
      });
    };

    setupWhatsAppSocketEvents();
    newSocket.connect();
    
    return newSocket;
  }, [showNotification]);

  useEffect(() => {
    mountedRef.current = true;

    if (isAuthenticated && wsspToken) {
      if (socketRef.current && socketRef.current.connected) {
        return;
      }
      
      if (socketRef.current && !socketRef.current.connected) {
        socketRef.current.connect();
        return;
      }
      
      const connection = createWhatsAppConnection();
      
      if (connection) {
        socketRef.current = connection;
        setSocket(connection);
      }
    } else {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        setSocket(null);
      }
      
      isConnectingRef.current = false;
    }

    return () => {
      mountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, wsspToken]);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, []);

  return (
    <WhatsAppSocketContext.Provider value={socket}>
      {children}
    </WhatsAppSocketContext.Provider>
  );
};

WhatsAppSocketProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
